import React from 'react';
import './VisibilityReportList.scss';
import { useTranslation } from 'react-i18next';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../../shared/helpers/filters/formatPeriodLabel';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { format } from 'date-fns';
import { NavLink } from 'react-router-dom';
import type { VisibilityReportListItem } from '@brand24-original/llm-types';
import { REPORTS } from '../../../shared/routes/path';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import NoResults from '../../../shared/components/common/no-results/NoResults';
import { brandLabel } from '../../../shared/components/report/brandLabel';
import { modelLabel } from '../../../shared/components/report/modelLabel';

interface VisibilityReportListProps {
  /** Potrzebny do zbudowania linku podglądu w kolumnie z datą. */
  projectId: number;
  reports: VisibilityReportListItem[];
  total: number;
  first: number;
  pageSize: number;
  onPage: (event: { first: number; rows: number }) => void;
  onOpen: (reportId: number) => void;
  onShare: (report: VisibilityReportListItem) => void;
  onDelete: (reportId: number) => void;
  busy?: boolean;
  /** Tryb zaznaczania dokłada kolumnę z checkboxami (jak na liście projektów). */
  selectionMode?: boolean;
  selectedIds?: number[];
  onToggleRow?: (reportId: number) => void;
  onToggleAll?: (checked: boolean) => void;
}

const VisibilityReportList: React.FC<VisibilityReportListProps> = ({
  projectId,
  reports,
  total,
  first,
  pageSize,
  onPage,
  onOpen,
  onShare,
  onDelete,
  busy,
  selectionMode = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
}) => {
  const { t, i18n } = useTranslation();
  const allOnPageSelected =
    reports.length > 0 && reports.every((report) => selectedIds.includes(report.id));

  const actions = (row: VisibilityReportListItem) => (
    <div className="buttons-list">
      <Button className="action-btn" title={t('visibilityReport.openBtn')} onClick={() => onOpen(row.id)}>
        <span className="ico--eye ico--xs" />
      </Button>
      <Button
        className="action-btn"
        disabled={busy}
        title={row.shareToken ? t('visibilityReport.shareRevoke') : t('visibilityReport.shareBtn')}
        onClick={() => onShare(row)}
      >
        <span className={`${row.shareToken ? 'ico--eye-off' : 'ico--copy'} ico--xs`} />
      </Button>
      <Button
        className="action-btn"
        disabled={busy}
        title={t('visibilityReport.deleteBtn')}
        onClick={() => onDelete(row.id)}
      >
        <span className="ico--trash ico--xs" />
      </Button>
    </div>
  );

  return (
    <div className="visibility-report-list">
      <DataTable
        value={reports}
        dataKey="id"
        tableClassName="table"
        className="table-wrapper table-wrapper--stack"
        responsiveLayout="stack"
        paginator={total > pageSize}
        lazy
        first={first}
        rows={pageSize}
        totalRecords={total}
        onPage={onPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        emptyMessage={() => (
          <NoResults icon="ico--report" titleKey="visibilityReport.emptyState" descKey="" />
        )}
      >
        {selectionMode && (
          <Column
            key="checkbox"
            exportable={false}
            bodyClassName="table__cell--stack-select"
            headerStyle={{ width: '20px', padding: '10px 0 10px 25px' }}
            bodyStyle={{ padding: '10px 0 10px 25px' }}
            header={<Checkbox checked={allOnPageSelected} onChange={(e) => onToggleAll?.(!!e.checked)} />}
            body={(row: VisibilityReportListItem) => (
              <Checkbox
                checked={selectedIds.includes(row.id)}
                onChange={() => onToggleRow?.(row.id)}
              />
            )}
          />
        )}
        <Column
          header={t('visibilityReport.colName')}
          headerClassName="table__header"
          bodyClassName="table__cell--stack-title"
          // Nazwa jest skrótem do podglądu. NavLink, a nie onClick — żeby działało otwieranie
          // w nowej karcie (ctrl/środkowy przycisk), tak jak nazwa projektu na liście projektów.
          body={(row: VisibilityReportListItem) => (
            <NavLink className="visibility-report-list__name" to={`${REPORTS}/${projectId}/${row.id}`}>
              {t('visibilityReport.reportName', {
                period: formatPeriodLabel(row.periodFrom, row.periodTo, periodLocaleFor(i18n.language)),
              })}
            </NavLink>
          )}
        />
        <Column
          header={t('visibilityReport.colBrand')}
          headerClassName="table__header"
          body={(row: VisibilityReportListItem) => brandLabel(row.brand) || '-'}
        />
        <Column
          header={t('visibilityReport.colPeriodType')}
          headerClassName="table__header"
          body={(row: VisibilityReportListItem) => t(`${row.periodType.toLowerCase()}Filter`)}
        />
        <Column
          header={t('visibilityReport.colModels')}
          headerClassName="table__header"
          body={(row: VisibilityReportListItem) =>
            row.models.length === 0
              ? t('visibilityReport.modelsAll')
              : row.models.map((model) => modelLabel(model)).join(', ')
          }
        />
        <Column
          header={t('visibilityReport.colDate')}
          headerClassName="table__header"
          body={(row: VisibilityReportListItem) => format(new Date(row.createdAt), 'yyyy-MM-dd / HH:mm')}
        />
        <Column
          header={t('visibilityReport.colActions')}
          body={(row: VisibilityReportListItem) => <MoreBtn children={actions(row)} rowId={row.id} />}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center table__cell--stack-actions"
          style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
        />
      </DataTable>
    </div>
  );
};

export default VisibilityReportList;
